/* 2026-02 · Assigned-ISBN Ledger — Admin panel.
 *
 * Groups every ISSUED ISBN by author → title, so admins can scan
 * "who has what" without paging through the flat records list.
 * Each edition (paperback/hardcover/ebook/audiobook) shows its ISBN,
 * format, publication date, retail price, and links to download the
 * EAN-13 barcode AND the QR-code image (which encodes the public
 * /book/{isbn} landing page, meant for the back-cover of a print
 * edition).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, BookOpen, Download, QrCode, Barcode, ExternalLink } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { formatIsbn } from '@/lib/format';

export default function IsbnAssignedLedger() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openAuthor, setOpenAuthor] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await adminApi.isbnLedger();
      setData(r);
      setOpenAuthor((cur) => cur || r.authors?.[0]?.author || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ledger.');
      setData({ authors: [], total_isbns: 0, total_authors: 0 });
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-isbn-ledger">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <BookOpen className="h-4 w-4 text-primary" /> Assigned ISBN Ledger
          </h3>
          <p className="mt-0.5 text-[10.5px] text-muted-foreground">
            {data ? `${data.total_isbns} issued ISBNs across ${data.total_authors} authors.` : 'Loading…'}
          </p>
        </div>
        <button type="button" onClick={load} disabled={busy}
                data-testid="admin-isbn-ledger-refresh"
                className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11px] hover:bg-secondary disabled:opacity-60">
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {!data && busy && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading ledger…
        </div>
      )}

      {data && data.authors.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center text-[12px] text-muted-foreground">
          No ISBNs have been issued yet. Once you allocate an ISBN from the Bank to an issued record, it will appear here grouped by author.
        </div>
      )}

      <div className="space-y-2">
        {(data?.authors || []).map((a) => (
          <AuthorCard key={a.author} author={a}
                       isOpen={openAuthor === a.author}
                       onToggle={() => setOpenAuthor(openAuthor === a.author ? null : a.author)}
          />
        ))}
      </div>
    </div>
  );
}

function AuthorCard({ author, isOpen, onToggle }) {
  return (
    <div className="rounded-md border border-border bg-card"
         data-testid={`admin-isbn-ledger-author-${author.author}`}>
      <button type="button" onClick={onToggle}
              data-testid={`admin-isbn-ledger-author-toggle-${author.author}`}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-secondary/40">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold">{author.author}</div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground">
            {author.title_count} title{author.title_count === 1 ? '' : 's'} · {author.isbn_count} ISBN{author.isbn_count === 1 ? '' : 's'}
          </div>
        </div>
        <span className="text-[10.5px] text-muted-foreground">{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && (
        <div className="space-y-2 border-t border-border p-3">
          {author.titles.map((t) => (
            <div key={t.title} className="rounded-sm border border-border bg-background p-2.5"
                 data-testid={`admin-isbn-ledger-title-${t.title}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-semibold">{t.title}</div>
                  {t.subtitle && (
                    <div className="mt-0.5 truncate text-[10.5px] text-muted-foreground">{t.subtitle}</div>
                  )}
                </div>
              </div>
              <div className="mt-2 space-y-1.5">
                {t.editions.map((e) => (
                  <EditionRow key={e.id} edition={e} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditionRow({ edition }) {
  const price = edition.retail_price_cents != null
    ? `$${(edition.retail_price_cents / 100).toFixed(2)}`
    : '—';
  const publicUrl = `/book/${edition.isbn}`;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-card/50 px-2 py-1.5 text-[10.5px]"
         data-testid={`admin-isbn-ledger-edition-${edition.id}`}>
      <span className="rounded-full bg-secondary px-1.5 py-[1px] font-semibold uppercase tracking-wider text-[9.5px]">
        {edition.format || '—'}
      </span>
      <span className="font-mono text-muted-foreground">{formatIsbn(edition.isbn)}</span>
      {edition.edition && (
        <span className="text-muted-foreground">ed. {edition.edition}</span>
      )}
      <span className="text-muted-foreground">{price}</span>
      {edition.publication_date && (
        <span className="text-muted-foreground">pub {edition.publication_date}</span>
      )}
      <div className="ml-auto flex items-center gap-1">
        <a href={publicUrl} target="_blank" rel="noreferrer"
           title="Open the public /book landing page"
           data-testid={`admin-isbn-ledger-open-public-${edition.id}`}
           className="inline-flex items-center gap-0.5 rounded-sm border border-border px-1.5 py-0.5 hover:bg-secondary">
          <ExternalLink className="h-3 w-3" /> View
        </a>
        <a href={adminApi.isbnBarcodeUrl(edition.id)} target="_blank" rel="noreferrer"
           title="Download the EAN-13 barcode PNG"
           data-testid={`admin-isbn-ledger-barcode-${edition.id}`}
           className="inline-flex items-center gap-0.5 rounded-sm border border-border px-1.5 py-0.5 hover:bg-secondary">
          <Barcode className="h-3 w-3" /> EAN-13
        </a>
        <a href={adminApi.isbnQrcodeUrl(edition.id)} target="_blank" rel="noreferrer"
           title="Download the QR-code PNG (encodes /book/{isbn})"
           data-testid={`admin-isbn-ledger-qrcode-${edition.id}`}
           className="inline-flex items-center gap-0.5 rounded-sm border border-border px-1.5 py-0.5 hover:bg-secondary">
          <QrCode className="h-3 w-3" /> QR
        </a>
      </div>
    </div>
  );
}
